/**
 * ADR-0047: the LIFECYCLE around `startSlackIngest` for opencode agents.
 *
 * `slack-ingest.ts` owns the Socket Mode loop itself; this owns when an agent
 * has one. The two are split because the loop is pure transport + governance
 * (testable against fakes, no filesystem, no manager state) whereas this half is
 * inherently stateful: one live socket per agent, restarted on rotation, stopped
 * on teardown.
 *
 * Where the config comes from
 * ---------------------------
 * The manager needs the same Slack env the Claude path hands its slack-channel
 * MCP. Rather than re-derive it (the refresh path's ChannelEnvOptions derivation
 * is long, and it is shared with the live Claude fleet, so forking it to serve
 * opencode would put the fleet's gating at risk), this reads back exactly what
 * provisioning wrote via the opencode adapter's `readChannelServerEnv`. One
 * source of truth, and it survives a manager restart because both files are on
 * disk. The tradeoff is that an agent whose channel credentials were never
 * written simply has no ingest, which is the correct outcome anyway.
 *
 * Restart semantics
 * -----------------
 * The loop is keyed on a fingerprint of its resolved params, so a token rotation
 * or a sender-policy change tears the old socket down and opens a new one, while
 * an unchanged config is a cheap no-op on every poll tick. A server respawn does
 * NOT need a restart: `injectOpencodeMessage` resolves the live port/password per
 * call, so the loop follows the new server on its own.
 */

import { createHash } from 'node:crypto';

import { readChannelServerEnv } from '@augmented/core/provisioning/frameworks/opencode/index.js';

import { injectOpencodeMessage } from './opencode-session.js';
import {
  startSlackIngest,
  resolveSlackIngestParamsFromEnv,
  resolveSlackBotUserId,
  type SlackIngestHandle,
  type SlackIngestParams,
  type FetchLike,
  type SlackSocketFactory,
} from './slack-ingest.js';

interface IngestEntry {
  /** Fingerprint of the params the live loop was started with. */
  key: string;
  handle: SlackIngestHandle;
}

/** codeName -> the agent's live ingest loop. */
const ingests = new Map<string, IngestEntry>();

/**
 * Stable fingerprint of the ingest's inputs. Any change (token rotation, sender
 * policy edit, peer roster change) yields a different key and forces a restart.
 * Hashed rather than stored raw so the bot/app tokens never reach a log line or
 * a heap dump of this map.
 *
 * `botUserId` is deliberately NOT part of it: it is derived from botToken via
 * auth.test, so an unchanged token means an unchanged id. Keying on params alone
 * lets the already-running case short-circuit BEFORE auth.test, which matters
 * because this runs on every poll tick and Slack rate-limits that endpoint.
 */
function fingerprint(params: SlackIngestParams): string {
  return createHash('sha256')
    .update(
      JSON.stringify({
        appToken: params.appToken,
        botToken: params.botToken,
        senderPolicy: params.senderPolicy,
        peerConfig: params.peerConfig,
        ackReaction: params.ackReaction ?? null,
        skipReaction: params.skipReaction ?? null,
      }),
    )
    .digest('hex');
}

export interface EnsureIngestOptions {
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  /** Test seams, forwarded to startSlackIngest. */
  fetchImpl?: FetchLike;
  socketFactory?: SlackSocketFactory;
  /** Test seam: override the materialized Slack env lookup. */
  readEnv?: (codeName: string, channelId: string) => Record<string, string> | null;
}

/**
 * Ensure the agent has exactly one Slack ingest loop matching its current
 * config. Idempotent: a no-op when a loop is already running with the same
 * params, so it is safe to call on every poll tick.
 *
 * Never throws. A misconfigured sender policy fails closed inside
 * `resolveSlackIngestParamsFromEnv` (by design - the same fail-closed parse the
 * Claude MCP does at boot), and that must degrade to "no Slack ingest for this
 * agent", not take down the manager's poll loop for every other agent on the
 * host.
 */
export async function ensureOpencodeSlackIngest(opts: EnsureIngestOptions): Promise<void> {
  const { codeName, agentId, log } = opts;
  const readEnv = opts.readEnv ?? readChannelServerEnv;

  let params: SlackIngestParams | null;
  let env: Record<string, string> | null;
  try {
    env = readEnv(codeName, 'slack');
    if (!env) {
      // No slack MCP entry: the channel was never provisioned or was removed.
      stopOpencodeSlackIngest(codeName, log);
      return;
    }
    params = resolveSlackIngestParamsFromEnv(env, { agentTeamId: env['AGT_TEAM_ID'] ?? null });
  } catch (err) {
    // Fail-closed policy parse rejected the config. Make sure any loop started
    // from a previously-valid config is torn down rather than left running on
    // stale policy.
    stopOpencodeSlackIngest(codeName, log);
    log(
      `[slack-ingest:${codeName}] not started: ${err instanceof Error ? err.message : String(err)}`,
    );
    return;
  }

  if (!params) {
    // No app/bot token: Socket Mode is impossible. Not an error.
    stopOpencodeSlackIngest(codeName, log);
    return;
  }

  const key = fingerprint(params);
  const existing = ingests.get(codeName);
  // The common case on a steady-state host: already running, config unchanged.
  // Returning here keeps the poll tick free of any Slack round-trip.
  if (existing?.key === key) return;

  const { id: botUserId, authFailed } = await resolveSlackBotUserId(params.botToken, opts.fetchImpl);
  if (authFailed) {
    // A definitively-rejected token cannot open a socket either, so starting the
    // loop would just spin on apps.connections.open. Leave no loop running.
    stopOpencodeSlackIngest(codeName, log);
    log(`[slack-ingest:${codeName}] not started: Slack bot token rejected (needs reconnect)`);
    return;
  }
  if (!botUserId) {
    // Transient auth.test failure. Start anyway and degrade: echo filtering is
    // weaker without the id, but a working channel beats no channel.
    log(`[slack-ingest:${codeName}] bot user id unresolved; echo filtering degraded`);
  }

  if (existing) {
    existing.handle.stop();
    ingests.delete(codeName);
    log(`[slack-ingest:${codeName}] config changed; restarting ingest`);
  }

  const handle = startSlackIngest({
    codeName,
    agentId,
    appToken: params.appToken,
    botToken: params.botToken,
    botUserId,
    senderPolicy: params.senderPolicy,
    peerConfig: params.peerConfig,
    inject: (msg) => injectOpencodeMessage(codeName, msg, { awaitReply: true }),
    log,
    ...(params.ackReaction ? { ackReaction: params.ackReaction } : {}),
    ...(params.skipReaction ? { skipReaction: params.skipReaction } : {}),
    ...(opts.fetchImpl ? { fetchImpl: opts.fetchImpl } : {}),
    ...(opts.socketFactory ? { socketFactory: opts.socketFactory } : {}),
  });
  ingests.set(codeName, { key, handle });
  log(`[slack-ingest:${codeName}] ingest started (policy=${params.senderPolicy.mode})`);
}

/** Stop and forget an agent's ingest loop. Safe to call when none is running. */
export function stopOpencodeSlackIngest(codeName: string, log?: (msg: string) => void): void {
  const entry = ingests.get(codeName);
  if (!entry) return;
  entry.handle.stop();
  ingests.delete(codeName);
  log?.(`[slack-ingest:${codeName}] ingest stopped`);
}

/** Stop every ingest loop (manager shutdown / drain). */
export function stopAllOpencodeSlackIngests(log?: (msg: string) => void): void {
  for (const codeName of [...ingests.keys()]) stopOpencodeSlackIngest(codeName, log);
}

/** Test/diagnostic seam: which agents currently have a live ingest loop. */
export function activeSlackIngestAgents(): string[] {
  return [...ingests.keys()];
}
